import { softPulseAnimation } from '@app/core/ui/animations';
import { Avatar, Box, useTheme } from '@mui/material';

interface Props {
  readonly width: number;
  readonly height: number;
  readonly logoUrl?: string | null;
  readonly stationName?: string;
}

/**
 * Soft-pulsing station avatar shown while a thumbnail image is loading.
 * Matches StationAvatar (logo or name initial) with a muted grayscale look.
 * Colors and pulse range follow the app MUI theme (light/dark).
 */
export function LoadingThumbnailPlaceholder({ width, height, logoUrl, stationName }: Props) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const logoSize = Math.min(44, Math.max(28, Math.round(height * 0.36)));
  const fallbackLetter = stationName?.charAt(0).toUpperCase() || '?';

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: `${width}px`,
        height: `${height}px`,
        bgcolor: isDark ? 'background.default' : 'grey.100',
      }}
    >
      <Box
        sx={{
          animation: softPulseAnimation,
          '--pulse-min': isDark ? 0.18 : 0.28,
          '--pulse-max': isDark ? 0.32 : 0.45,
          lineHeight: 0,
        }}
      >
        <Avatar
          src={logoUrl || undefined}
          alt={stationName ? `${stationName} logo` : 'Station logo'}
          sx={{
            width: logoSize,
            height: logoSize,
            fontSize: `${logoSize * 0.3125}px`,
            filter: isDark ? 'grayscale(1) brightness(0.85)' : 'grayscale(1)',
            border: 'none',
            bgcolor: isDark ? 'grey.800' : 'grey.200',
            color: 'grey.500',
          }}
        >
          {fallbackLetter}
        </Avatar>
      </Box>
    </Box>
  );
}
